import {Component, EventEmitter, HostListener, Input, OnChanges, OnInit, Output, SimpleChanges} from '@angular/core';
import {truncateDecimals} from '../../../Custom-Directives-Pipes/truncate-decimals.pipe';
import {MatTableDataSource} from "@angular/material/table";
import {PageEvent} from "@angular/material/paginator";
import {LabSampleModel} from "../../../models/lab-manager/lab-sample";
import {data} from "autoprefixer";


@Component({
    selector: 'app-samples-table',
    templateUrl: './samples-table.component.html',
    styleUrls: ['./samples-table.component.css'],
    standalone: false
})
export class SamplesTableComponent implements OnInit, OnChanges{
  currentPage: number = 0;
  @Input() callResponse: any;
  dataSource: MatTableDataSource<any> = new MatTableDataSource<any>();
  displayedColumns: string[] = [
    'checkbox',
    'farm',
    'field',
    'hectare',
    'sample',
    'sampleType',
    'dateCreated',
    'createdBy',
    'linkedGroup'
  ];
  @Input() isDialog: boolean;
  @Input() submissionStatus: number;
  @Input() pinTypeId: number = 0;
  pageSize: number = 9999;
  tableHeight: string = (window.innerHeight - 500) + 'px';
  totalElements: number = 0;
  @Input() selectedSamples: LabSampleModel[] = [];
  @Input() mergeGroups: any[] = [];
  @Output() emitSelectedSamples: EventEmitter<any> = new EventEmitter<any>();
  @Output() removeSample: EventEmitter<number> = new EventEmitter<number>();
  @Output() paginateChange: EventEmitter<any> = new EventEmitter<any>();
  @Output() mergeSamples: EventEmitter<any> = new EventEmitter<any>();
  @Output() unmergeSamples: EventEmitter<number> = new EventEmitter<number>();

  /**
   * A request to see what a merge group contains.
   *
   * Emitted rather than handled here because the group data lives on the parent,
   * which owns mergeGroups. Carries the row as well so the parent can read the
   * primary field and farm off it: the group payload does not include them, and
   * the primary IS a row here.
   */
  @Output() viewMergeGroup: EventEmitter<{ group: any; element: any }> = new EventEmitter<{ group: any; element: any }>();

  /** The full ordered list of sample ids, emitted when the user changes the order. */
  @Output() orderChanged: EventEmitter<number[]> = new EventEmitter<number[]>();

  /**
   * The position shown against a row: 1..n by its place in the list.
   *
   * Computed rather than read from the row. Until somebody orders a submission
   * every sortOrder is null in the database, and showing a column of blanks reads
   * as broken rather than as "not set yet". The list is already in the order the
   * server decided, so the index IS the position either way.
   */
  positionOf(row: any): number {
    return this.dataSource.data.indexOf(row) + 1;
  }

  /**
   * Move a row to a typed position, shifting everything it passes.
   *
   * Assigning a position displaces rather than duplicates, so two rows can never
   * hold the same number and nothing is ever bumped to a next-free value. A row
   * moving down pushes the rows it passes up, and one moving up pushes them down.
   * The count never changes, so the list stays 1..n with no gaps.
   *
   * Out of range clamps: above the row count goes last, below 1 goes first.
   */
  moveTo(row: any, typed: any): void {
    const rows = this.dataSource.data;
    const from = rows.indexOf(row);
    if (from === -1) return;

    const requested = Number(typed);
    if (!Number.isFinite(requested)) {
      // Restores the number they overtyped, since the cell shows a computed value.
      this.dataSource = new MatTableDataSource<any>([...rows]);
      return;
    }

    const to = Math.min(Math.max(Math.round(requested), 1), rows.length) - 1;
    if (to === from) return;

    const reordered = [...rows];
    reordered.splice(to, 0, reordered.splice(from, 1)[0]);

    // Reassigned so mat-table sees a new array. trackBy keeps the DOM rows.
    this.dataSource = new MatTableDataSource<any>(reordered);
    this.orderChanged.emit(reordered.map(r => r.id));
  }

  @HostListener('window:resize', ['$event'])
  onResize() {
    this.tableHeight = (window.innerHeight - 500) + 'px';
  }

  constructor() {
  }

  /**
   * Field size, with the crop definition's cultivar size in brackets when the
   * sample has one: '8.22 (2.55)'.
   *
   * Uses ?? rather than a truthiness check so a recorded size of 0 is shown
   * rather than falling through to the next branch as if it were absent.
   *
   * cultivarSize is resolved server-side from the field's ACTIVE season, not
   * from the sample's own crop definition link. It is therefore null once that
   * season has ended, and on a multi-cultivar field it is the oldest definition
   * rather than the one this sample belongs to.
   */
  hectareDisplay(element: any): string {
    const fieldSize = element?.pinFieldSize ?? element?.pinSamplesPinFieldSize;
    if (fieldSize == null) return '-';

    const field = truncateDecimals(fieldSize, 2);
    const cultivar = element?.cultivarSize;
    return cultivar != null ? field + ' (' + truncateDecimals(cultivar, 2) + ')' : field;
  }

  ngOnInit(): void {
  }



  onPaginateChange(event: PageEvent) {
    // if(this.pageSize != event.pageSize) {
    //   this.selectedSamples = [];
    // }
    this.currentPage = event.pageIndex;
    this.pageSize = event.pageSize;
    this.paginateChange.emit({
      currentPage: this.currentPage,
      pageSize: this.pageSize
    })
  }

  emitChanges() {
    this.emitSelectedSamples.emit(this.selectedSamples);
  }

  removeSampleEmit(sample: any) {
    this.removeSample.emit(sample)
  }

  getMergeCount(sampleId: number): number {
    const group = this.mergeGroups?.find(g => g.primarySampleId === sampleId);
    return group ? group.linkedCount + 1 : 0;
  }

  /** The group this sample is the PRIMARY of, or null. Members are not rows here. */
  mergeGroupFor(sampleId: number): any {
    return this.mergeGroups?.find(g => g.primarySampleId === sampleId) ?? null;
  }

  /**
   * Opens the merge panel for a row, if that row heads a group.
   *
   * Silent on an ordinary row. Row click reaches this for every row, and a
   * sample that is in no group has nothing to show.
   */
  emitViewMergeGroup(element: any): void {
    const group = this.mergeGroupFor(element?.id);
    if (group) {
      this.viewMergeGroup.emit({ group, element });
    }
  }

  isMerged(sampleId: number): boolean {
    return this.mergeGroups?.some(g => g.primarySampleId === sampleId || g.linkedSampleIds?.includes(sampleId)) ?? false;
  }

  emitMerge() {
    this.mergeSamples.emit(this.selectedSamples);
    this.selectedSamples = [];
  }

  /**
   * Drops the tick boxes, and with them the action bar above the table.
   *
   * The selection lives here rather than on the parent, so the parent cannot reach it directly.
   * Merge clears it inline because it acts immediately. Remove goes through a confirmation
   * dialog first, so clearing on emit would also clear it when somebody cancels; the parent
   * calls this once the removal has actually happened instead.
   *
   * Without it the samples went but the selection stayed, so the count and the Remove and Merge
   * buttons sat there referring to rows that no longer existed. Reported 28/10/2026.
   */
  clearSelection(): void {
    this.selectedSamples = [];
  }

  emitUnmerge(primarySampleId: number) {
    this.unmergeSamples.emit(primarySampleId);
  }

  selectAllSamples(event: any): void {
    this.dataSource.data.forEach(item => {
      this.selectSample(event, item);
    });
  }

  hasSample(sample: LabSampleModel) {
    return this.selectedSamples.findIndex(s => s.id === sample.id) !== -1;
  }

  // hasAllSamples() {
  //   if(this.selectedSamples.length === 0) {
  //     return {all: false, intermediate: false};
  //   } else {
  //     let count = 0;
  //     for(let data of this.dataSource.data) {
  //       if(this.selectedSamples.findIndex(s => s.id === data.id) !== -1) {
  //         count++;
  //       }
  //     }
  //
  //     if(count === 0) {
  //       return {all: false, intermediate: false};
  //     } else if(count === this.dataSource.data.length) {
  //       return {all: true, intermediate: false};
  //     } else {
  //       return {all: false, intermediate: true};
  //     }
  //   }
  // }

  isAllSelected(): boolean {
    return this.dataSource.data.length > 0 &&
        this.dataSource.data.every(r => this.selectedSamples.findIndex(s => s.id === r.id) !== -1);
  }

// True if *some* (but not all) visible rows are selected
  isSomeSelected(): boolean {
    return this.dataSource.data.some(r => this.selectedSamples.findIndex(s => s.id === r.id) !== -1) &&
        !this.isAllSelected();
  }

  selectSample(event: any, sample: LabSampleModel) {
    if (event.checked) {
      // Add to the array if checked
      if(this.selectedSamples.findIndex(s => s.id === sample.id) === -1) {
        this.selectedSamples.push(sample);
      }
    } else {
      // Remove from the array if unchecked
      const index = this.selectedSamples.findIndex(s => s.id === sample.id);
      if (index > -1) {
        this.selectedSamples.splice(index, 1);
      }
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if(changes?.callResponse) {
      this.totalElements = this.callResponse.totalElements;
      this.dataSource = new MatTableDataSource<any>(this.callResponse.content);
    }

    // Rebuilt from scratch rather than mutated.
    //
    // This used to splice, shift, push and filter the same array across separate
    // change cycles, so which columns appeared depended on the order the inputs
    // happened to arrive in. The depth column was the clearest casualty: inserted
    // when callResponse changed, then silently dropped if submissionStatus arrived
    // afterwards and reset the array to a list that had no depth in it.
    this.displayedColumns = this.buildColumns();
  }

  /**
   * The columns for the current inputs. A pure function of isDialog, pinTypeId and
   * submissionStatus, so the same inputs always produce the same columns.
   *
   * Assigned in ngOnChanges rather than exposed as a getter: matHeaderRowDef and
   * matRowDef re-read it on every change detection pass, and a getter returning a
   * fresh array each time would have mat-table rebuilding its columns constantly.
   */
  private buildColumns(): string[] {
    const columns: string[] = [];

    // Selection is only offered while the submission is still editable.
    if (!(this.submissionStatus >= 2)) {
      columns.push('checkbox');
    }

    columns.push('farm', 'field', 'hectare', 'sample');

    // Soil samples carry a depth and a rock percentage. pinTypeId 4 is the soil pin type and 2 the soil
    // lab type; the two callers pass the same idea through different inputs.
    if (this.pinTypeId === 4 || this.pinTypeId === 2) {
      columns.push('depth', 'rockPct');
    }

    // Shown in both views. The picker's rows carry a bag number too, and somebody
    // choosing which samples to add is often working from the bags in front of them.
    columns.push('bagNumber');

    // The add-samples picker shows neither the order, the reference, nor actions.
    // Those rows are not on a submission yet, so they have no order to set.
    if (!this.isDialog) {
      columns.push('sortOrder', 'sampleReference');
    }

    columns.push('sampleType', 'dateCreated', 'createdBy', 'linkedGroup');

    if (!this.isDialog && !(this.submissionStatus >= 2)) {
      columns.push('action');
    }

    return columns;
  }

  /**
   * Identity for a row, so reordering moves rows rather than re-creating all of
   * them. Without it a reorder rebuilds the whole table, which is slow and drops
   * focus from the cell being edited.
   */
  trackBySampleId(_index: number, row: any): number {
    return row?.id;
  }
}
